import { OreStack, OreText } from '@edenredespana/oreneta'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AutoEnrollmentDefaultConditionsModel } from '@/AutoEnrollment/domain'
import {
  Step3GetProductDocument,
  Step3ServicesTable,
  Step3SubProductsTable,
} from '@/AutoEnrollment/ui/atoms'
import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import {
  ContractNewProductsStep1Actions,
  ContractNewProductsStep1ProductWrapper,
} from '@/Contract/ui/atoms'
import { useContractNewProducts } from '@/Contract/ui/context'
import { Step1Form } from '@/Contract/ui/hooks'
import { edenredProducts } from '@/Shared/domain'

export const ContractNewProductsStep1 = (): JSX.Element => {
  const { t } = useTranslation()

  const {
    getCurrentStepForm,
    documents: { model: productsConditions, documents },
  } = useContractNewProducts()

  const form = getCurrentStepForm() as UseFormReturn<Step1Form>
  const watchProducts = form.watch('products')

  const handleProductToggle = (productCode: edenredProducts): void => {
    const currentProduct = form.getValues('products')
    const productIndex = currentProduct.findIndex(
      product => product.productId === productCode,
    )

    if (productIndex === -1) return

    const currentSubProducts = currentProduct[productIndex].productServices

    if (!currentSubProducts) return

    const areAllSubProductsActive = currentSubProducts.every(
      subProduct => subProduct.isActive,
    )

    currentSubProducts.forEach(subProduct => {
      subProduct.isActive = !areAllSubProductsActive
    })

    form.setValue('products', currentProduct)
  }

  const handleSubProductsToggle = (
    productCode: edenredProducts,
    subProductServiceCode: number,
  ): void => {
    const currentProduct = form.getValues('products')
    const productIndex = currentProduct.findIndex(
      product => product.productId === productCode,
    )

    if (productIndex === -1) return

    const currentSubProducts = currentProduct[productIndex].productServices

    if (!currentSubProducts) return

    const subProductIndex = currentSubProducts?.findIndex(
      subProduct => subProduct.serviceId === subProductServiceCode,
    )

    if (subProductIndex === -1) return

    const isActive = currentSubProducts[subProductIndex].isActive
    currentSubProducts[subProductIndex].isActive = !isActive

    form.setValue('products', currentProduct)
  }

  return (
    <FormProvider {...form}>
      <form>
        <OreStack placeContent="stretch" space="small">
          {productsConditions &&
            productsConditions.map(
              (product: AutoEnrollmentDefaultConditionsModel, i: number) => (
                <ContractNewProductsStep1ProductWrapper
                  key={`product-${i}`}
                  product={product}
                >
                  <OreStack space="large" placeContent="stretch">
                    {product?.subProducts?.length ? (
                      <Step3SubProductsTable
                        key={`subproduct-condition-${i}`}
                        subProducts={product.subProducts}
                        disabledState={
                          !watchProducts
                            ?.find(p => p.productId === product.productCode)
                            ?.productServices?.some(service => service.isActive) || false
                        }
                        parentProductCode={product.productCode}
                      />
                    ) : null}
                    {product?.services?.length ? (
                      <OreStack
                        key={`product-services-${i}`}
                        space="small"
                        placeContent="stretch"
                      >
                        <Step3ServicesTable
                          services={product.services}
                          disabledState={
                            !watchProducts
                              ?.find(p => p.productId === product.productCode)
                              ?.productServices?.some(service => service.isActive) ||
                            false
                          }
                        />
                        <OreText tone="neutral-600" size="text-xs">
                          {'* '}
                          {t(
                            AutoEnrollmentTranslation.productsConditions.servicesTable
                              .disclaimer[product.productCode],
                          )}
                        </OreText>
                      </OreStack>
                    ) : null}
                    <Step3GetProductDocument
                      productId={product.productCode}
                      documentsForSigning={documents?.documentsForSigning ?? []}
                    />
                  </OreStack>
                </ContractNewProductsStep1ProductWrapper>
              ),
            )}
          <ContractNewProductsStep1Actions />
        </OreStack>
      </form>
    </FormProvider>
  )
}
